/**
 * FilterSection Component
 * 협력사 주정산내역 검색 필터 섹션
 */
import React, { useMemo } from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import MultiSelectDropdown from '@/shared/components/forms/MultiSelectDropdown';
import { Supplier } from '@/features/partner/supplierStatus/types/supplier.types';
import { generateFridayOptions, getSettlementPeriodForFriday, formatDate } from '@/shared/utils/weeklySettlementDateUtils';
import '@/shared/styles/search-form.css';
import '../styles/PartnerSettlementHistory.css';

interface FilterSectionProps {
    filters: {
        acctStDate: string;
        acctEdDate: string;
        suppId: string;
    };
    suppliers: Supplier[];
    isSupplierUser: boolean;
    onFilterChange: (filters: { acctStDate: string; acctEdDate: string; suppId: string }) => void;
    onSearch: () => void;
    onReset: () => void;
    onExcelDownload: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
    filters,
    suppliers,
    isSupplierUser,
    onFilterChange,
    onSearch,
    onReset,
    onExcelDownload
}) => {
    /**
     * 엔터키 입력 시 검색 실행
     */
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    /**
     * 금요일 옵션 생성 (현재 달의 모든 금요일)
     */
    const fridayOptions = useMemo(() => generateFridayOptions(), []);

    /**
     * 선택 모드 상태 (friday: 금요일 선택, custom: 직접 입력)
     */
    const [selectionMode, setSelectionMode] = React.useState<'friday' | 'custom'>('friday');

    /**
     * 선택된 금요일 목록 (다중 선택)
     */
    const [selectedFridays, setSelectedFridays] = React.useState<string[]>([]);

    /**
     * 금요일 다중 선택 변경 핸들러 (자동 적용)
     */
    const handleFridaysChange = (fridayValues: string[]) => {
        setSelectedFridays(fridayValues);
        
        // 선택된 금요일이 있으면 자동으로 날짜 적용
        if (fridayValues.length > 0) {
            const periods = fridayValues.map(fValue => {
                const friday = new Date(fValue);
                return getSettlementPeriodForFriday(friday);
            });

            const startDate = periods.reduce((earliest, period) => 
                period.startDate < earliest ? period.startDate : earliest, periods[0].startDate);
            const endDate = periods.reduce((latest, period) => 
                period.endDate > latest ? period.endDate : latest, periods[0].endDate);

            onFilterChange({
                ...filters,
                acctStDate: formatDate(startDate),
                acctEdDate: formatDate(endDate)
            });
        }
    };

    /**
     * 정산 기간 표시 텍스트
     */
    const settlementPeriodText = useMemo(() => {
        if (!filters.acctStDate || !filters.acctEdDate) return '';
        
        const startDate = new Date(filters.acctStDate);
        const endDate = new Date(filters.acctEdDate);
        
        return `${startDate.getMonth() + 1}월 ${startDate.getDate()}일 ~ ${endDate.getMonth() + 1}월 ${endDate.getDate()}일`;
    }, [filters.acctStDate, filters.acctEdDate]);

    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierOptions: SelectOption[] = useMemo(() => {
        return suppliers.map((supplier) => ({
            value: supplier.id,
            label: supplier.name
        }));
    }, [suppliers]);

    return (
        <div className="card search-card partner-settlement-filter">
            <div className="card-body">
                <div className={`search-form settlement-search-form ${isSupplierUser ? 'supplier-view' : ''}`}>
                    {/* 조회일자 */}
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--color-gray-200)' }}>
                                <label className="form-label" style={{ margin: 0 }}>
                                    <i className="fas fa-calendar-alt" />
                                    조회일자
                                </label>
                                
                                {/* 정산기간 표시 - 오른쪽 끝 */}
                                {settlementPeriodText && (
                                    <span style={{
                                        fontSize: '0.7rem',
                                        color: '#04B374',
                                        fontWeight: '500',
                                        paddingBottom: '0.625rem',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <i className="fas fa-calendar-check" style={{ fontSize: '0.65rem', marginRight: '3px' }} />
                                        {settlementPeriodText}
                                    </span>
                                )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginTop: '8px', minHeight: '38px' }}>
                                {/* 선택 모드 토글 */}
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setSelectionMode('friday')}
                                        style={{
                                            padding: '6px 12px',
                                            fontSize: '0.8rem',
                                            border: 'none',
                                            borderRadius: '4px',
                                            backgroundColor: selectionMode === 'friday' ? '#04B374' : '#f5f5f5',
                                            color: selectionMode === 'friday' ? '#fff' : '#666',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            transition: 'all 0.2s ease',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        금요일
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSelectionMode('custom')}
                                        style={{
                                            padding: '6px 12px',
                                            fontSize: '0.8rem',
                                            border: 'none',
                                            borderRadius: '4px',
                                            backgroundColor: selectionMode === 'custom' ? '#04B374' : '#f5f5f5',
                                            color: selectionMode === 'custom' ? '#fff' : '#666',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            transition: 'all 0.2s ease',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        직접입력
                                    </button>
                                </div>

                                {/* 금요일 선택 모드 */}
                                {selectionMode === 'friday' && (
                                    <div style={{ flex: 1, maxWidth: '400px', height: '38px' }}>
                                        <MultiSelectDropdown
                                            options={fridayOptions.map(option => ({
                                                value: option.value,
                                                label: option.label,
                                                description: option.settlementPeriod
                                            }))}
                                            selectedValues={selectedFridays}
                                            onChange={handleFridaysChange}
                                            placeholder="금요일을 선택하세요..."
                                        />
                                    </div>
                                )}

                                {/* 직접 입력 모드 */}
                                {selectionMode === 'custom' && (
                                    <div className="date-range-inputs" style={{ flex: 1, maxWidth: '400px', height: '38px' }}>
                                        <DateInput
                                            id="filterAcctStDate"
                                            value={filters.acctStDate}
                                            onChange={(value) => onFilterChange({ ...filters, acctStDate: value })}
                                            onKeyPress={handleKeyPress}
                                        />
                                        <span className="date-separator">~</span>
                                        <DateInput
                                            id="filterAcctEdDate"
                                            value={filters.acctEdDate}
                                            onChange={(value) => onFilterChange({ ...filters, acctEdDate: value })}
                                            onKeyPress={handleKeyPress}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 협력사 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-building" />
                                협력사
                            </label>
                            <SearchableSelect
                                options={supplierOptions}
                                value={filters.suppId}
                                onChange={(value) => onFilterChange({ ...filters, suppId: value })}
                                placeholder="협력사 검색..."
                                disabled={isSupplierUser}
                                showAllOption={true}
                                allOptionLabel="전체"
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button className="btn btn-secondary" onClick={onReset}>
                            <i className="fas fa-redo" />
                            초기화
                        </button>
                        {!isSupplierUser && (
                            <button className="btn btn-success" onClick={onExcelDownload}>
                                <i className="fas fa-file-excel" />
                                엑셀
                            </button>
                        )}
                        <button
                            className="btn btn-primary"
                            onClick={onSearch}
                        >
                            <i className="fas fa-search" />
                            검색
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
